import { Translator } from '../../types/ApiResponseTypes'
import {
  SubscriptionListType,
  SubscriptionStatusCounts,
  SubscriptionType,
  SubscriptionTypeInternet,
  SubscriptionTypeMobile,
} from '@/types/subscription/SubscriptionType'
import {
  isInternetProduct,
  isMobileProduct,
  isSubscriptionListResponse,
  isSubscriptionStatusCountsResponse,
  subscriptionResponse,
  SubscriptionStatusCountsResponse,
} from '@/types/subscription/SubscriptionResponse'
import { SubscriptionAddonResponse } from '@/types/addons/SubscriptionAddonResponse'
import { SubscriptionAddonStatus, SubscriptionAddonType } from '@/types/addons/SubscriptionAddonType'

class SubscriptionResponseTranslator implements Translator<SubscriptionListType> {
  convert(response: any): SubscriptionListType {
    if (isSubscriptionListResponse(response)) {
      const objects: Array<SubscriptionType> = []
      if (Array.isArray(response.subscriptions)) {
        response.subscriptions.forEach((subscription: subscriptionResponse) => {
          objects.push(this.convertToSubscriptionModel(subscription))
        })
        return { total: response.total, page: response.page, subscriptions: objects }
      } else {
        return { total: 0, page: 0, subscriptions: [] }
      }
    }
    return { total: 0, page: 0, subscriptions: [] }
  }

  formatMobileProduct(response: subscriptionResponse): SubscriptionTypeMobile {
    return {
      id: response.product.mobileId ?? 0,
      label: response.product.label ?? response.desc,
      telephoneNumber: response.product.telephoneNumber ?? '',
      simCardNumber: response.product.simCardNumber ?? '',
      simType: response.product.simType ?? '',
      imsiNumber: response.product.imsiNumber ?? '',
      puk1: response.product.mobilePuk1 ?? '',
      puk2: response.product.mobilePuk2 ?? '',
      activateMms: response.product.activateMms ?? 0,
      blockInternational: response.product.blockInternational ?? 0,
      blockVas: response.product.blockVas ?? 0,
      outOfBundleSms: response.product.outOfBundleSms ?? 0,
      dailyBLockingLimit: response.product.dailyBLockingLimit ?? 0,
      firstOutOfBundleLimit: response.product.firstOutOfBundleLimit ?? 0,
      monthlyBlockingLimit: response.product.monthlyBlockingLimit ?? 0,
      monthlyWarningLimit: response.product.monthlyWarningLimit ?? 0,
      secondOutOfBundleLimit: response.product.secondOutOfBundleLimit ?? 0,
      saleId: response.product.saleId ?? 0,
      statusId: response.statusId ?? 0,
      status: response.product.status ?? '',
      bundle: {
        data: 'data' in response.product.bundle ? parseInt(response.product.bundle.data) : 0,
        voice: 'voice' in response.product.bundle ? parseInt(response.product.bundle.voice) : 0,
        sms: 'sms' in response.product.bundle ? parseInt(response.product.bundle.sms) : 0,
      },
      editable: response.product.editable ?? 0,
      price: response.product.price ?? 0,
      outOfBundleCost: response.product.outOfBundleCost ?? 0,
      activationDate: response.product.activationDate ?? null,
      terminationDate: response.product.terminationDate ?? null,
      isDeliveryContact: response.product.isDeliveryContact === 1 || response.isDeliveryContact === 1,
    }
  }

  formatInternetProduct(response: subscriptionResponse): SubscriptionTypeInternet {
    return {
      id: response.product.connectionId,
      address: {
        street: response.product.address.street ?? '',
        streetNumber: response.product.address.streetNumber ?? '',
        streetNumberAlpha: response.product.address.streetNumberAlpha ?? '',
        bus: response.product.address.bus ?? '',
        zipCode: response.product.address.zipCode ?? '',
        city: response.product.address.city ?? '',
        countryId: response.product.address.countryId ?? 0,
        country: response.product.address.country ?? '',
      },
      circuitId: response.product.circuitId ?? '',
      label: response.product.label ?? '',
      lex: response.product.lex ?? '',
      utac: response.product.utac ?? '',
      uni: response.product.uni ?? '',
      userName: response.product.username ?? '',
      passWord: response.product.password ?? '',
      fixedIp: response.product.fixedIp ?? '',
      fixedIpAddress: response.product.fixedIpAddress ?? '',
      useIpV6: response.product.useIpV6 ?? '',
      saleId: response.product.saleId ?? 0,
      statusId: response.statusId ?? 0,
      status: response.product.status ?? '',
      connectionType: response.product.type ?? '',
    }
  }
  convertToSubscriptionModel(response: subscriptionResponse): SubscriptionType {
    let product = undefined

    if (isMobileProduct(response.product)) {
      product = this.formatMobileProduct(response)
    } else if (isInternetProduct(response.product)) {
      product = this.formatInternetProduct(response)
    }

    return {
      id: response.subscriptionId,
      type: response.type.toLowerCase(),
      desc: response.desc,
      statusId: response.statusId,
      status: response.status,
      product: product,
    }
  }

  convertToAddonArray(response: SubscriptionAddonResponse): SubscriptionAddonType[] {
    if (!Array.isArray(response)) return []
    return response.map((addon) => this.convertToAddonModel(addon))
  }

  convertToAddonModel(response: SubscriptionAddonResponse): SubscriptionAddonType {
    return {
      id: response.articleId ?? 0,
      description: response.description ?? '',
      status: this.convertToAddonStatus(response.status),
      price: response.price ?? 0.0,
    }
  }

  convertToAddonStatus(status: string | undefined): SubscriptionAddonStatus {
    return Object.values(SubscriptionAddonStatus).includes(status as SubscriptionAddonStatus)
      ? (status as SubscriptionAddonStatus)
      : SubscriptionAddonStatus.NOT_ACTIVATED
  }

  convertToStatusCountsModel(response: SubscriptionStatusCountsResponse): SubscriptionStatusCounts {
    if (isSubscriptionStatusCountsResponse(response)) {
      return {
        mobile: response.mobile,
        internet: response.internet,
      }
    } else {
      throw Error('unable to fetch product counts')
    }
  }
}
export default new SubscriptionResponseTranslator()
